import { FC } from 'react'
import { SectionHeroProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import { Link, Media, PortableText } from '@portfolio/components'
import { OpenNewTab } from '@portfolio/icons'

const SectionHeroContainer = styled('section', {
  '@lg': {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'minmax(auto, 1fr) minmax(auto, 1fr)',
    rowGap: 24,
    height: '100vh',
  },
})

const SectionHeroTitlesContainer = styled('div', {
  gridRow: '1 / span 1',
  gridColumn: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  alignItems: 'center',
  paddingBlockStart: 'calc(var(--sizes-headerHeight) + 88px)',
  paddingBlockEnd: 104,
  paddingInline: 20,
  width: '100%',

  '@lg': {
    gap: 16,
    paddingInline: '$30',
    paddingBlockEnd: 0,
    paddingBlockStart: 'calc(var(--sizes-headerHeight) + 104px)',
    alignItems: 'flex-start',
  },
  '@xxl': {
    paddingInline: 0,
    maxWidth: 'calc($maxWidthDesktop / 2)',
    marginInline: 'auto 0',
  },
})

const SectionHeroSubtitle = styled('h2', {
  projectFont: 'heading08',
  fontSize: '16px',
  gridRow: '1 / span 1',
  gridColumn: 1,
  color: '$white',
  paddingInline: 20,
  width: 'fit-content',
  display: 'flex',
  height: '36px',
  padding: '0px 16px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  borderRadius: '$rMax',
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(15px)',
})

const SectionHeroTitle = styled('h1', {
  projectFont: 'heading01',
  gridRow: '1 / span 1',

  color: '$white',
  maxWidth: 650,
})

const SectionHeroImageMobile = styled('div', {
  position: 'relative',
  marginBlockEnd: 48,
  aspectRatio: '1/1',
  gridRow: '1 / span 3',

  '@lg': {
    display: 'none',
  },
})

const SectionHeroImageDesktop = styled('div', {
  display: 'none',

  '@lg': {
    display: 'block',
    gridRow: '1 / span 3',
    gridColumn: 2,
    height: '100%',
  },
})

const SectionHeroDescription = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 30,
  paddingInline: 20,
  projectFont: 'heading07',
  color: '$white',

  '@lg': {
    alignSelf: 'flex-start',
    maxWidth: '70%',
    paddingInline: 0,
  },
})

const SectionHeroLink = styled('div', {
  width: 'fit-content',
  marginTop: 30,
  paddingInline: 20,
  '@lg': {
    alignSelf: 'flex-start',
    margin: '0',
    paddingInline: 0,
  },
})

const SectionHeroContentContainer = styled('div', {
  width: '100%',
  '@lg': {
    paddingInline: '$30',
    gridRow: '2 / span 1',
    gridColumn: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    gap: 30,
  },
  '@xxl': {
    paddingInline: 0,
    maxWidth: 'calc($maxWidthDesktop / 2)',
    marginInline: 'auto 0',
  },
})

const LinkLabel = styled('span', {
  display: 'block',
  marginRight: 8,
})

export const SectionHero: FC<SectionHeroProps> = ({
  title,
  subtitle,
  text,
  media,
  link,
  linkLabel,
}) => {
  return (
    <SectionHeroContainer>
      <SectionHeroTitlesContainer>
        {subtitle ? (
          <SectionHeroSubtitle>{subtitle}</SectionHeroSubtitle>
        ) : null}
        <SectionHeroTitle>{title}</SectionHeroTitle>
      </SectionHeroTitlesContainer>
      <SectionHeroImageMobile>
        {media?.mediaPayload && (
          <Media
            {...media}
            sizes="(min-width: 1000px) 0px, 100vw"
            layout="fill"
          />
        )}
      </SectionHeroImageMobile>
      <SectionHeroImageDesktop>
        {media?.mediaPayload && (
          <Media
            {...media}
            sizes="(min-width: 1000px) 75vw, 0px"
            layout="fill"
          />
        )}
      </SectionHeroImageDesktop>
      <SectionHeroContentContainer>
        {text ? (
          <PortableText
            value={text}
            appearance="plain"
            additionalComponents={{
              block: {
                normal: ({ children }) => (
                  <SectionHeroDescription>{children}</SectionHeroDescription>
                ),
              },
            }}
          />
        ) : null}
        {link && linkLabel && (
          <SectionHeroLink>
            <Link {...link} appearance="containedTransparent">
              <LinkLabel>{linkLabel}</LinkLabel> <OpenNewTab />
            </Link>
          </SectionHeroLink>
        )}
      </SectionHeroContentContainer>
    </SectionHeroContainer>
  )
}
